/* Reading a verification link, and the states the screen can be in. ADR-014.
 *
 * **Deliberately `reset.ts`'s shapes.** A verification link and a reset link are the same object —
 * a single-use token in a URL that a mail client may mangle — so the two screens have the same four
 * states and the same reasons for keeping them apart. A second vocabulary for one problem is the
 * two-vocabularies failure this codebase records at five sites.
 */

/** The token from `?token=…`, or the fact that there wasn't one.
 *
 * **A link with no token is its own state and is not the same as a rejected one.** "Missing" means
 * the URL was mangled on the way here — truncated by a mail client, or copied without its query
 * string — and telling that reader their link expired sends them to ask for another that will
 * arrive equally broken. "Rejected" means the platform read a token and refused it, which is nearly
 * always expiry. `reset.ts` keeps the same two apart for the same reason and is tested for it.
 */
export type VerificationToken =
  { readonly kind: 'missing' } | { readonly kind: 'present'; readonly token: string };

export function tokenFromQuery(search: string): VerificationToken {
  const raw = new URLSearchParams(search).get('token');
  if (raw === null) return { kind: 'missing' };
  const token = raw.trim();
  return token === '' ? { kind: 'missing' } : { kind: 'present', token };
}

/** Whether a signup the server accepted can actually reach anybody.
 *
 * **`delivered: false` means the account exists and no mail went out.** Rendering "check your
 * email" over that leaves somebody waiting for a message nobody sent, which is the worst outcome
 * this flow has — the same asymmetry `reset.ts`'s `Delivery` argues, and the reason the API reports
 * the field at all rather than letting a 201 imply delivery.
 */
export type SignupDelivery = 'sent' | 'undeliverable' | 'already_verified';

export function deliveryOf(outcome: {
  readonly verification_required: boolean;
  readonly delivered: boolean;
}): SignupDelivery {
  // Checked before `delivered`, because the two are independent and this one decides what the
  // screen is *about*: an address proved through the pre-signup code flow needs no mail at all, so
  // reporting it as undeliverable would send somebody to an administrator over a working account.
  if (!outcome.verification_required) return 'already_verified';
  return outcome.delivered ? 'sent' : 'undeliverable';
}

/** Whether the router state says an address was just verified.
 *
 * `announcesPasswordChange`'s shape one flow over, and its reasoning: the confirmation travels in
 * the router's own state rather than in the query string, so a bookmark of `/login` cannot announce
 * a verification that never happened. It guards rather than casts, because router state is whatever
 * the previous screen put there.
 */
export function announcesEmailVerified(state: unknown): boolean {
  if (typeof state !== 'object' || state === null) return false;
  return (state as Record<string, unknown>)['emailVerified'] === true;
}

/** The RFC 9457 `type` a login refuses an unverified account with.
 *
 * **A constant compared against the problem `type`, never against the status code and never against
 * the sentence.** A 403 also means "you may not do that" on every other route in the platform, so
 * branching on the status alone would make a genuine authorization refusal render a resend button;
 * and matching on the message would couple this screen to the server's wording, so the sentence
 * could never be reworded and a translation would break the branch. `ApiError.extras` carries the
 * whole problem document for exactly this.
 */
export const EMAIL_NOT_VERIFIED = 'https://lumiq.dev/problems/email-not-verified';

/** Whether a failed login failed *because* the address is unverified.
 *
 * This is the one login refusal a screen is allowed to act on, and the server can only give it to a
 * caller who supplied the correct password — a wrong password against an unverified account answers
 * the same generic 401 everything else does. So a `true` here means the person reading it owns the
 * account, which is what makes offering them a resend safe rather than a way to mail strangers.
 */
export function isEmailNotVerified(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) return false;
  const candidate = error as { readonly extras?: unknown };
  if (typeof candidate.extras !== 'object' || candidate.extras === null) return false;
  return (candidate.extras as Record<string, unknown>)['type'] === EMAIL_NOT_VERIFIED;
}
